import { useQuery } from '@tanstack/react-query';
import { apiFetch } from '@/lib/apiClient';
import { configKeys } from '@/api/keys';
import type {
  UIMetadata,
  KeywordGroup,
  KeywordOption,
  CategoryOption,
  ToggleGroup,
  ToggleOption,
  VibeOption,
  Limits,
} from '@/types/uiMetadata';
import { formatKeyFallback } from '@/types/categories';

// =============================================================================
// Primary Hook: useUIMetadata
// =============================================================================

/**
 * Hook to fetch UI metadata configuration.
 *
 * This is the primary hook for accessing all UI configuration data.
 * Data is cached for 24 hours since it rarely changes.
 *
 * @example
 * ```tsx
 * const { data: uiMetadata, isLoading } = useUIMetadata();
 *
 * // Access keyword groups
 * const keywordGroups = uiMetadata?.keywords;
 *
 * // Access categories
 * const categories = uiMetadata?.categories;
 *
 * // Access toggle groups
 * const toggleGroups = uiMetadata?.toggles;
 *
 * // Access vibes
 * const vibes = uiMetadata?.vibes;
 * ```
 */
export function useUIMetadata() {
  return useQuery({
    queryKey: configKeys.uiMetadata(),
    queryFn: () => apiFetch<UIMetadata>('/config/ui-metadata/'),
    staleTime: 24 * 60 * 60 * 1000, // 24 hours
    gcTime: 24 * 60 * 60 * 1000, // Keep in cache for 24 hours
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  });
}

// =============================================================================
// Convenience Hooks (derived from useUIMetadata)
// =============================================================================

/**
 * Convenience hook to access keyword groups from UI metadata.
 *
 * @example
 * ```tsx
 * const { data: keywordGroups } = useKeywordGroups();
 * keywordGroups?.forEach(group => {
 *   console.log(group.displayName, group.options.length);
 * });
 * ```
 */
export function useKeywordGroups() {
  const { data, ...rest } = useUIMetadata();
  return { data: data?.keywords, ...rest };
}

/**
 * Convenience hook to access categories from UI metadata.
 *
 * @example
 * ```tsx
 * const { data: categories } = useCategories();
 * categories?.forEach(cat => {
 *   console.log(cat.key, cat.displayName);
 * });
 * ```
 */
export function useCategories() {
  const { data, ...rest } = useUIMetadata();
  return { data: data?.categories, ...rest };
}

/**
 * Convenience hook to access toggle groups from UI metadata.
 *
 * @example
 * ```tsx
 * const { data: toggleGroups } = useToggleGroups();
 * toggleGroups?.forEach(group => {
 *   console.log(group.displayName, group.options.length);
 * });
 * ```
 */
export function useToggleGroups() {
  const { data, ...rest } = useUIMetadata();
  return { data: data?.toggles, ...rest };
}

/**
 * Convenience hook to access vibes from UI metadata.
 *
 * @example
 * ```tsx
 * const { data: vibes } = useVibes();
 * vibes?.forEach(vibe => {
 *   console.log(vibe.label, vibe.icon, vibe.gradientStart);
 * });
 * ```
 */
export function useVibes() {
  const { data, ...rest } = useUIMetadata();
  return { data: data?.vibes, ...rest };
}

/**
 * Convenience hook to access limits from UI metadata.
 *
 * @example
 * ```tsx
 * const { data: limits } = useLimits();
 * const maxKeywords = limits?.maxKeywords; // e.g., 30
 * ```
 */
export function useLimits() {
  const { data, ...rest } = useUIMetadata();
  return { data: data?.limits, ...rest };
}

// =============================================================================
// Helper Functions: Keywords
// =============================================================================

/**
 * Get a keyword option by key from UI metadata.
 * Returns undefined if not found.
 */
export function getKeywordOption(
  key: string,
  metadata: UIMetadata | undefined
): KeywordOption | undefined {
  if (!metadata) return undefined;

  for (const group of metadata.keywords) {
    const option = group.options.find(o => o.key === key);
    if (option) return option;
  }
  return undefined;
}

/**
 * Get display name for a keyword key from UI metadata.
 * Falls back to formatted key if not found.
 */
export function getKeywordDisplayName(
  key: string,
  metadata: UIMetadata | undefined
): string {
  const option = getKeywordOption(key, metadata);
  return option?.displayName ?? formatKeyFallback(key);
}

/**
 * Get the group key for a keyword from UI metadata.
 * Returns null if not found.
 */
export function getKeywordGroupKey(
  key: string,
  metadata: UIMetadata | undefined
): string | null {
  if (!metadata) return null;

  for (const group of metadata.keywords) {
    if (group.options.some(o => o.key === key)) {
      return group.key;
    }
  }
  return null;
}

/**
 * Get the group display name for a keyword group.
 */
export function getKeywordGroupDisplayName(
  groupKey: string,
  metadata: UIMetadata | undefined
): string {
  if (!metadata) return formatKeyFallback(groupKey);

  const group = metadata.keywords.find(g => g.key === groupKey);
  return group?.displayName ?? formatKeyFallback(groupKey);
}

/**
 * Get icon for a keyword key from UI metadata.
 * Returns null if not found.
 */
export function getKeywordIcon(
  key: string,
  metadata: UIMetadata | undefined
): string | null {
  const option = getKeywordOption(key, metadata);
  return option?.icon ?? null;
}

/**
 * Get gradient colors for a keyword key from UI metadata.
 * Returns null if not found.
 */
export function getKeywordGradient(
  key: string,
  metadata: UIMetadata | undefined
): [string, string] | null {
  const option = getKeywordOption(key, metadata);
  if (option?.gradientStart && option?.gradientEnd) {
    return [option.gradientStart, option.gradientEnd];
  }
  return null;
}

/**
 * Get all keyword keys belonging to a specific group.
 */
export function getKeywordsByGroup(
  groupKey: string,
  metadata: UIMetadata | undefined
): string[] {
  if (!metadata) return [];

  const group = metadata.keywords.find(g => g.key === groupKey);
  return group?.options.map(o => o.key) ?? [];
}

// =============================================================================
// Helper Functions: Categories
// =============================================================================

/**
 * Get a category option by key from UI metadata.
 * Returns undefined if not found.
 */
export function getCategoryOption(
  key: string,
  metadata: UIMetadata | undefined
): CategoryOption | undefined {
  if (!metadata) return undefined;
  return metadata.categories.find(c => c.key === key);
}

/**
 * Get display name for a category key from UI metadata.
 * Falls back to formatted key if not found.
 */
export function getCategoryDisplayName(
  key: string,
  metadata: UIMetadata | undefined
): string {
  const option = getCategoryOption(key, metadata);
  return option?.displayName ?? formatKeyFallback(key);
}

/**
 * Get icon for a category key from UI metadata.
 * Returns null if not found.
 */
export function getCategoryIcon(
  key: string,
  metadata: UIMetadata | undefined
): string | null {
  const option = getCategoryOption(key, metadata);
  return option?.icon ?? null;
}

/**
 * Get gradient colors for a category key from UI metadata.
 * Returns null if not found.
 */
export function getCategoryGradient(
  key: string,
  metadata: UIMetadata | undefined
): [string, string] | null {
  const option = getCategoryOption(key, metadata);
  if (option?.gradientStart && option?.gradientEnd) {
    return [option.gradientStart, option.gradientEnd];
  }
  return null;
}

// =============================================================================
// Helper Functions: Toggles
// =============================================================================

/**
 * Get a toggle option by key from UI metadata.
 * Returns undefined if not found.
 */
export function getToggleOption(
  key: string,
  metadata: UIMetadata | undefined
): ToggleOption | undefined {
  if (!metadata) return undefined;

  for (const group of metadata.toggles) {
    const option = group.options.find(o => o.key === key);
    if (option) return option;
  }
  return undefined;
}

/**
 * Get display name for a toggle key from UI metadata.
 * Falls back to formatted key if not found.
 */
export function getToggleDisplayName(
  key: string,
  metadata: UIMetadata | undefined
): string {
  const option = getToggleOption(key, metadata);
  return option?.displayName ?? formatKeyFallback(key);
}

/**
 * Get the group key for a toggle from UI metadata.
 * Returns null if not found.
 */
export function getToggleGroupKey(
  key: string,
  metadata: UIMetadata | undefined
): string | null {
  if (!metadata) return null;

  for (const group of metadata.toggles) {
    if (group.options.some(o => o.key === key)) {
      return group.key;
    }
  }
  return null;
}

/**
 * Get the group display name for a toggle group.
 */
export function getToggleGroupDisplayName(
  groupKey: string,
  metadata: UIMetadata | undefined
): string {
  if (!metadata) return formatKeyFallback(groupKey);

  const group = metadata.toggles.find(g => g.key === groupKey);
  return group?.displayName ?? formatKeyFallback(groupKey);
}

/**
 * Get icon for a toggle key from UI metadata.
 * Returns null if not found.
 */
export function getToggleIcon(
  key: string,
  metadata: UIMetadata | undefined
): string | null {
  const option = getToggleOption(key, metadata);
  return option?.icon ?? null;
}

/**
 * Get gradient colors for a toggle key from UI metadata.
 * Returns null if not found.
 */
export function getToggleGradient(
  key: string,
  metadata: UIMetadata | undefined
): [string, string] | null {
  const option = getToggleOption(key, metadata);
  if (option?.gradientStart && option?.gradientEnd) {
    return [option.gradientStart, option.gradientEnd];
  }
  return null;
}

// =============================================================================
// Helper Functions: Vibes
// =============================================================================

/**
 * Get a vibe option by key from UI metadata.
 * Returns undefined if not found.
 */
export function getVibeOption(
  key: string,
  metadata: UIMetadata | undefined
): VibeOption | undefined {
  if (!metadata) return undefined;
  return metadata.vibes.find(v => v.key === key);
}

/**
 * Get label for a vibe key from UI metadata.
 * Falls back to formatted key if not found.
 */
export function getVibeLabel(
  key: string,
  metadata: UIMetadata | undefined
): string {
  const option = getVibeOption(key, metadata);
  return option?.label ?? formatKeyFallback(key);
}

/**
 * Get icon for a vibe key from UI metadata.
 * Returns null if not found.
 */
export function getVibeIcon(
  key: string,
  metadata: UIMetadata | undefined
): string | null {
  const option = getVibeOption(key, metadata);
  return option?.icon ?? null;
}

/**
 * Get gradient colors for a vibe key from UI metadata.
 * Returns null if not found.
 */
export function getVibeGradient(
  key: string,
  metadata: UIMetadata | undefined
): [string, string] | null {
  const option = getVibeOption(key, metadata);
  if (option?.gradientStart && option?.gradientEnd) {
    return [option.gradientStart, option.gradientEnd];
  }
  return null;
}

// =============================================================================
// Re-exports for type convenience
// =============================================================================

export type {
  UIMetadata,
  KeywordGroup,
  KeywordOption,
  CategoryOption,
  ToggleGroup,
  ToggleOption,
  VibeOption,
  Limits,
};
